import Monitoring from "../monitoring/monitoring";
import store from "../store";
import { isImpersonating } from "../utilities";
import View from "../view";
import { WorkflowStep } from "./workflowStep";

class Workflow {
    private workflowSteps: Array<WorkflowStep> | null = null;

    async getSteps(): Promise<Array<WorkflowStep>> {
        if (this.workflowSteps != null) {
            return Promise.resolve(this.workflowSteps);
        }

        let i = 0;
        while (store.state.configuration?.workflow == null) {
            await new Promise(resolve => setTimeout(resolve, 50));
            i++;
        }

        this.workflowSteps = store.state.configuration.workflow.pages;
        return Promise.resolve(this.workflowSteps);
    }

    getWorkflowStep = async function(view: View): Promise<WorkflowStep | null> {
        const steps = await this.getSteps();

        const viewIndex = steps.findIndex(ws => ws.view == view);

        return viewIndex == -1
            ? null
            : steps[viewIndex];
    }

    getCurrentStep = async function(): Promise<WorkflowStep | null> {
        const currentView = store.state.view;
        return this.getWorkflowStep(currentView);
    }

    getNextWorkflowStep = async function(view: View): Promise<WorkflowStep | null> {
        const currentStep = await this.getWorkflowStep(view);

        if (currentStep == null) {
            const steps = await this.getSteps();
            let stepNames = "";
            for (const step of steps) {
                stepNames += step.text + ", ";
            }

            throw new Error(`Unknown workflow step, the requested view was: ${view.toString()}. The current steps were: ${stepNames}`);
        }

        const steps = await this.getSteps();
        const currentStepIndex = steps.indexOf(currentStep);
        const nextStepIndex = currentStepIndex + 1;
        
        if (nextStepIndex > steps.length - 1) {
            if (view == View.Finish && store.state.configuration.finishDisplaysNextButton) {
                return { view: View.GuiidePlus, resource: 'guiideplus', text: '', allowDirectAccessFromPreviousPage: false, number: nextStepIndex }
            }

            return null;
        }

        return steps[nextStepIndex];
    }

    getNextStep = async function(): Promise<WorkflowStep | null> {
        const currentView = store.state.view;

        if (currentView == View.Options) {
            return null;
        }

        return this.getNextWorkflowStep(currentView);
    }

    goToPreviousWorkflowStep = async function (): Promise<void> {
        await this.goToWorkflowStepPrivate((currentWorklflowIndex) => { return currentWorklflowIndex - 1; })
    }

    goToNextWorkflowStep = async function (): Promise<void> {
        const currentStep = await this.getCurrentStep();
        if (currentStep != null) {
            Monitoring.trackEvent('stepCompleted', {
                stepName: currentStep.text
            });
        }

        await this.goToWorkflowStepPrivate((currentWorklflowIndex) => { return currentWorklflowIndex + 1; })
    }

    goToWorkflowStep = async function(resource: string): Promise<void> {
        const steps = await this.getSteps();
        const requestedStepIndex = steps.findIndex(ws => ws.resource == resource);
        const workflowStep = steps[requestedStepIndex];
        this.changeToStep(workflowStep);
    }

    goToWorkflowStepPrivate = async function (stepOperator: (currentWorkflowIndex: number) => number): Promise<void> {
        const currentView = store.state.view;

        const steps = await this.getSteps();

        const currentViewWorkflowIndex = steps.findIndex(ws => ws.view == currentView);

        if (currentViewWorkflowIndex == -1) {
            return;
        }

        const nextViewWorkflowIndex = stepOperator(currentViewWorkflowIndex);

        let nextView = steps[currentViewWorkflowIndex];
        if (nextViewWorkflowIndex >= 0 && nextViewWorkflowIndex < steps.length) {
            nextView = steps[nextViewWorkflowIndex];
        }

        this.changeToStep(nextView);
    }

    changeToStep(workflowStep: WorkflowStep) {
        if (isImpersonating()) {
            store.commit("updateView", workflowStep.view);

            const retiree = store.state.retiree;

            store.commit("updateRetiree", null);

            setTimeout(() => { 
                store.commit("updateRetiree", retiree);
            }, 50);
            
        } else {
            window.location.replace(`/${workflowStep.resource}`);
        }
    }
}

const workflow = new Workflow();

export { workflow };
